import { Component, computed, input } from '@angular/core';
import { carryOverKind } from '../../../utils/time-off-calendar.utils';
import { formatWeekdayDate } from '../../../utils/time-off-days.utils';

/**
 * The blue carry-over box (PBI 5770 AC#3): which days of a request drew from vacation carry-over,
 * labelled "Carry-Over" when every countable day did and "Partial Carry-Over" when only some did.
 *
 * Shared by the calendar's event dialog and the list's details dialog (PBI 6682 AC 4), so the two
 * can never describe the same request differently. Renders nothing when no day drew from
 * carry-over, so hosts need no guard of their own.
 */
@Component({
  selector: 'app-time-off-carry-over-summary',
  standalone: true,
  templateUrl: './time-off-carry-over-summary.component.html',
  styleUrls: ['./time-off-carry-over-summary.component.scss'],
})
export class TimeOffCarryOverSummaryComponent {
  /** Dates (yyyy-MM-dd) within the request that drew from vacation carry-over. */
  readonly carryOverDays = input<string[] | undefined>(undefined);
  /** The request's countable-day total, which full versus partial is measured against. */
  readonly daysOff = input<number>(0);

  readonly dates = computed(() => this.carryOverDays() ?? []);
  readonly hasCarryOver = computed(() => this.dates().length > 0);

  /** Same full-versus-partial rule as the calendar title suffix (single source of truth). */
  readonly label = computed(() =>
    carryOverKind(this.dates().length, this.daysOff()) === 'partial'
      ? 'Partial Carry-Over'
      : 'Carry-Over',
  );

  /** Comma-separated, human-formatted list of the carry-over dates. */
  readonly datesText = computed(() =>
    this.dates().map(formatWeekdayDate).join(', '),
  );
}
